<template>
  <div
    class="d-flex flex-column pa-3 rounded"
    :style="{ gap: '8px', backgroundColor: '#ECEDEE' }"
  >
    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      multiple
      :style="{ display: 'none' }"
      @change="onFilesSelected"
    />

    <div class="d-flex align-start" :style="{ gap: '8px' }">
      <v-textarea
        v-model="text"
        clearable
        flat
        hide-details="auto"
        :hint="
          images.length
            ? `Máximo ${COMMENT_MAX_IMAGES} imágenes por comentario (${images.length}/${COMMENT_MAX_IMAGES})`
            : ''
        "
        no-resize
        :persistent-hint="images.length ? true : false"
        :placeholder="
          isEditing ? 'Escribe para actualizar tu comentario' : placeholder
        "
        rows="4"
        solo
        @click:clear="text = ''"
      />

      <div class="d-flex flex-column align-center" :style="{ gap: '6px' }">
        <v-tooltip left>
          <template #activator="{ on, attrs }">
            <v-btn
              color="#FFFFFF"
              :disabled="images.length >= 10"
              :elevation="0"
              fab
              x-small
              v-bind="attrs"
              v-on="on"
              @click="$refs.fileInput.click()"
            >
              <v-icon color="#E20809">mdi-image-plus</v-icon>
            </v-btn>
          </template>
          <span>Cargar Imágenes</span>
        </v-tooltip>

        <v-tooltip v-if="isEditing" left>
          <template #activator="{ on, attrs }">
            <v-btn
              color="#E20809"
              :elevation="0"
              fab
              x-small
              v-bind="attrs"
              v-on="on"
              @click="onCancel"
            >
              <v-icon color="#FFFFFF">mdi-close</v-icon>
            </v-btn>
          </template>
          <span>Cancelar</span>
        </v-tooltip>

        <v-tooltip left>
          <template #activator="{ on, attrs }">
            <v-btn
              color="#1BA13C"
              :disabled="!text"
              :elevation="0"
              fab
              x-small
              v-bind="attrs"
              v-on="on"
              @click="onSubmit"
            >
              <v-icon color="#FFFFFF"> mdi-check </v-icon>
            </v-btn>
          </template>
          <span>{{ isEditing ? 'Guardar' : 'Publicar' }}</span>
        </v-tooltip>
      </div>
    </div>

    <v-slide-x-reverse-transition>
      <div v-if="images.length">
        <v-slide-x-reverse-transition
          group
          class="d-flex flex-wrap mt-2"
          :style="{ gap: '8px' }"
        >
          <div
            v-for="(image, index) in images"
            :key="image.id"
            :style="{ position: 'relative' }"
          >
            <img
              height="50"
              width="50"
              :src="image.preview"
              :style="{
                objectFit: 'cover',
                borderRadius: '4px',
                cursor: 'pointer',
              }"
              @click="$emit('openGallery', { images: galleryImages, index })"
            />
            <v-btn
              icon
              x-small
              :style="{
                backgroundColor: '#E20809 !important',
                position: 'absolute',
                right: '-8px',
                top: '-8px',
              }"
              @click.stop="removeImage(index)"
            >
              <v-icon x-small color="#FFFFFF">mdi-close</v-icon>
            </v-btn>
          </div>
        </v-slide-x-reverse-transition>
      </div>
    </v-slide-x-reverse-transition>
  </div>
</template>

<script>
  import {
    COMMENT_MAX_IMAGES,
    COMMENT_MAX_SIZE_KB,
    COMMENT_MIN_SIZE_KB,
  } from '@/core/constants/schedule/media';

  export default {
    name: 'EventHistoryCommentEditor',
    provide() {
      return { form: null };
    },
    props: {
      value: { type: String, default: '' },
      initialImages: { type: Array, default: () => [] },
      isEditing: { type: Boolean, default: false },
      placeholder: {
        type: String,
        default: 'Escribe para publicar un nuevo comentario...',
      },
    },
    data() {
      return {
        COMMENT_MAX_IMAGES,
        text: this.value,
        images: [],
        removedImageIds: [],
      };
    },
    computed: {
      galleryImages() {
        return this.images.map((img) => ({
          src: img.existing ? img.url : img.preview,
        }));
      },
    },
    watch: {
      value(val) {
        this.text = val;
      },
      initialImages: {
        immediate: true,
        handler(images) {
          this.images
            .filter((i) => !i.existing)
            .forEach((i) => URL.revokeObjectURL(i.preview));

          this.images = (images ?? []).map((img) => ({
            id: img.id,
            file: null,
            preview: img.thumb,
            url: img.url,
            existing: true,
          }));
        },
      },
    },
    beforeDestroy() {
      this.images
        .filter((i) => !i.existing)
        .forEach((i) => URL.revokeObjectURL(i.preview));
    },
    methods: {
      onFilesSelected(e) {
        const files = Array.from(e.target.files);
        const maxSizeMB = (COMMENT_MAX_SIZE_KB / 1024).toFixed(2);

        for (const file of files) {
          const sizeKB = file.size / 1024;
          const sizeMB = (sizeKB / 1024).toFixed(2);

          if (this.images.length >= COMMENT_MAX_IMAGES) {
            this.$toast.error(
              `Máximo ${COMMENT_MAX_IMAGES} imágenes por comentario`
            );
            break;
          }

          if (sizeKB < COMMENT_MIN_SIZE_KB || sizeKB > COMMENT_MAX_SIZE_KB) {
            this.$toast.error(
              `${file.name} debe pesar máximo de ${maxSizeMB} MB. Tu foto pesa ${sizeMB} MB`
            );
            continue;
          }

          this.images.push({
            id: `${file.name}-${Date.now()}-${Math.random()}`,
            file,
            preview: URL.createObjectURL(file),
            url: null,
            existing: false,
          });

          e.target.value = '';
        }
      },
      removeImage(index) {
        const img = this.images[index];
        if (img.existing) {
          this.removedImageIds.push(img.id);
        } else {
          URL.revokeObjectURL(img.preview);
        }

        this.images.splice(index, 1);
      },
      onSubmit() {
        this.$emit('submit', {
          text: this.text,
          newFiles: this.images.filter((i) => !i.existing).map((i) => i.file),
          removedImageIds: this.removedImageIds,
        });
      },
      onCancel() {
        this.$emit('cancel');
      },
      reset() {
        this.images
          .filter((i) => !i.existing)
          .forEach((i) => URL.revokeObjectURL(i.preview));
        this.text = '';
        this.images = [];
      },
    },
  };
</script>
